<template>
  <div class="resa-picker">
    <div class="widget-title" v-if="!hasPrice">
      <span>{{$t('room.picker.title')}}</span>
    </div>
    <div class="price-recap" v-if="hasPrice">
      <span v-if="nightCount < 2">{{$t('room.picker.title-active.singular-main', nightCount)}}</span>
      <span v-else>{{$t('room.picker.title-active.plural-main', nightCount)}}</span>
      <small v-if="travelerCount < 2">{{$t('room.picker.title-active.singular-second', travelerCount)}}</small>
      <small v-else>{{$t('room.picker.title-active.plural-second', travelerCount)}}</small>
    </div>
    <div class="room-date-picker">
      <div class="border-container">
        <div class="start-date">
          <div class="label-picker" @click="openPicker('start')">
            <span>{{$t('room.picker.form.start')}}</span>
            <span class="value">{{ startDate }}</span>
          </div>
        </div>
        <div class="end-date">
          <div class="label-picker" @click="openPicker('end')">
            <span>{{$t('room.picker.form.end')}}</span>
            <span class="value empty">{{ endDate }}</span>
          </div>
        </div>
        <div class="travelers">
          <div class="label-picker" @click="openTravelers">
            <span>{{$t('room.picker.form.travelers.title')}}</span>
            <span class="value">
            <template v-if="travelerCount > 1">{{$t('room.picker.form.travelers.plural', travelerCount)}}</template>
            <template v-else>{{$t('room.picker.form.travelers.singular', travelerCount)}}</template>
          </span>
          </div>
        </div>
      </div>
      <div class="calendar-drop" @click="closePicker" v-if="pickerOpen"></div>
      <div class="calendar-popup" v-if="pickerOpen">
        <div class="border-container only-dates">
          <div class="start-date" @click="setPicker('start')">
            <div class="label-picker" :class="{empty: pickerMode !== 'start', active: pickerMode === 'start'}">
              <span>{{$t('room.picker.form.start')}}</span>
              <span class="value">{{ startDate }}</span>
            </div>
          </div>
          <div class="end-date" @click="setPicker('end')">
            <div class="label-picker" :class="{empty: pickerMode !== 'end', active: pickerMode === 'end'}">
              <span>{{$t('room.picker.form.end')}}</span>
              <span class="value empty">{{ endDate }}</span>
            </div>
          </div>
        </div>
        <div class="center">
          <v-calendar
              v-if="pickerMode !== 'end'"
              is-expanded
              :disabled-dates="lockIntervalsStart"
              :attributes="calendarAttributes"
              :columns="$screens({ default: 1, md: 2 })"
              v-on:dayclick="handlePickerData"
              v-on:daymouseenter="handleDayHover"
              color="gray"
              :value="pickerMode === 'start' && start ? start.format('YYYY-MM-DD') : null"
          />
          <v-calendar
              is-expanded
              v-if="pickerMode === 'end'"
              :disabled-dates="endIntervalReduced"
              :attributes="calendarAttributes"
              :columns="$screens({ default: 1, md: 2 })"
              v-on:dayclick="handlePickerData"
              v-on:daymouseenter="handleDayHover"
              color="gray"
              :value="pickerMode === 'start' && start ? start.format('YYYY-MM-DD') : null"
          />
        </div>

      </div>
      <div class="traveler-drop" @click="closeTravelers" v-if="travelersOpen"></div>
      <div class="traveler-popup" v-if="travelersOpen">
        <div class="border-container only-travelers">
          <div class="travelers">
            <div class="label-picker active">
              <span>{{$t('room.picker.form.travelers.title')}}</span>
              <span class="value">
                <template v-if="travelerCount > 1">{{$t('room.picker.form.travelers.plural', travelerCount)}}</template>
                <template v-else>{{$t('room.picker.form.travelers.singular', travelerCount)}}</template>
              </span>
            </div>
          </div>
        </div>
        <div class="traveler-picker-data">
          <div class="list-item">
            <div class="name-group">
              <span class="item-name">{{ $t('v2.travelers.adults.title') }}</span>
              <span class="item-desc">{{ $t('v2.travelers.adults.desc') }}</span>
            </div>
            <div class="picker-group">
              <minus-svg :disabled="adults <= minAdults" v-on:click="addAdults(-1)"/>
              <div class="counter">{{ adults }}</div>
              <plus-svg :disabled="adults >= maxAdults" v-on:click="addAdults(1)"/>
            </div>
          </div>
          <div class="list-item">
            <div class="name-group">
              <span class="item-name">{{ $t('v2.travelers.children.title') }}</span>
              <span class="item-desc">{{ $t('v2.travelers.children.desc') }}</span>
            </div>
            <div class="picker-group">
              <minus-svg :disabled="children <= minChildren" v-on:click="addChildren(-1)"/>
              <div class="counter">{{ children }}</div>
              <plus-svg :disabled="children >= maxChildren" v-on:click="addChildren(1)"/>
            </div>
          </div>
          <div class="list-item">
            <div class="name-group">
              <span class="item-name">{{ $t('v2.travelers.babies.title') }}</span>
              <span class="item-desc">{{ $t('v2.travelers.babies.desc') }}</span>
            </div>
            <div class="picker-group">
              <minus-svg :disabled="babies <= minBabies" v-on:click="addBabies(-1)"/>
              <div class="counter">{{ babies }}</div>
              <plus-svg :disabled="babies >= maxBabies" v-on:click="addBabies(1)"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="action-part" v-if="!hasPrice">
      <button class="btn-rounded" :class="{'btn-white': loading}"
              :disabled="!start || !end"
              @click="sendCheckDisponibilities" v-if="!autoLoad">
        <template v-if="!loading">{{$t('room.picker.buttons.check')}}</template>
        <template v-else>
          <loader/>
        </template>
      </button>
    </div>
    <div class="action-part" v-if="hasPrice">
      <template v-if="filtered">
        <div class="info-block errored">
          <p>
            {{
              $t('tunnel.step1.sameBloc.start')
            }}{{ $t('tunnel.step1.sameBloc.' + (this.roomProvider === 1 ? 'TR' : 'Pars')) }}
            {{
              $t('tunnel.step1.sameBloc.middle')
            }}{{
              $t('tunnel.step1.sameBloc.' + (this.roomProvider === 1 ? 'Pars' : 'TR'))
            }}{{ $t('tunnel.step1.sameBloc.end') }}</p>
        </div>
      </template>
      <template v-else>
        <button class="btn-rounded btn-white" @click="sendCheckDisponibilities" v-if="!autoLoad">
          {{$t('room.picker.buttons.change-dates')}}
        </button>
        <span class="info center" v-if="pickedPrice === null">
          {{$t('room.picker.buttons.pick-price')}}
        </span>
        <div class="price-list" :class="{'has-select': pickedPrice !== null}">
          <div class="price-ctn"
               v-for="(price, index) in computedPrices"
               :key="price.id"
               @click="pickPrice(price)"
               :class="{active: pickedPrice && pickedPrice.id === price.id}"
               :item="price" :index="index">
            <div class="price-label"
                 :class="{'with-bg': needBackground(`#${price.text.color}`)}"
                 :style="{color: price.text.color ? `#${price.text.color}` : 'var(--darker-red)'}"
                 v-if="price.text.left_label">
              <div class="price-bg"
                   :style="{background: price.text.color ? `#${price.text.color}` : 'var(--darker-red)'}"></div>
              <span>{{ price.text.left_label }}</span>
            </div>
            <div class="offer-infos">
              <div class="offer-title">
                <span>{{ price.text.title }}</span>
                <span class="description">
                  {{ price.text.description }}
                </span>
              </div>
              <div class="offer-price" v-if="!price.total.altPrice || (price.total.hostingPrice + price.total.additionalPrice + price.total.extraAdditionalPrice) >= (price.total.altPrice.hostingPrice + price.total.altPrice.additionalPrice + price.total.altPrice.extraAdditionalPrice)">
                <span class="price">
                  {{ (price.total.hostingPrice + price.total.additionalPrice + price.total.extraAdditionalPrice) | decimalPrice(false) }}€
                </span>
                <small class="price-info">{{$t('room.picker.form.price-tax-free')}}</small>
              </div>
              <div class="offer-price alt-price" v-else>
                <span class="alt">{{ (price.total.altPrice.hostingPrice + price.total.altPrice.additionalPrice + price.total.altPrice.extraAdditionalPrice) | decimalPrice(false) }}€</span>
                <span class="price">
                  {{ (price.total.hostingPrice + price.total.additionalPrice + price.total.extraAdditionalPrice) | decimalPrice(false) }}€
                </span>
                <small class="price-info">{{$t('room.picker.form.price-tax-free')}}</small>
              </div>
            </div>
          </div>
        </div>
        <button class="btn-rounded" :disabled="canBook" @click="book">
          {{$t('room.picker.buttons.book')}}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import * as moment from 'moment'
import Loader from "../../../components/loader.component";
import MinusSvg from "../../../components/svg/minus-svg";
import PlusSvg from "../../../components/svg/plus-svg";
import {contrastRatio} from "../../../helpers/color.helper";
import {mapState} from "vuex";

export default {
  name: "resa-picker",
  components: {PlusSvg, MinusSvg, Loader},
  props: {
    update: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  computed: {
    ...mapState({
      id: s => s.room.room.id,
      roomId: s => s.room.room.id,
      roomMaxTravelers: s => s.room.room.capacity_max,
      lockIntervalsStart: s => {
        const bookings = s.shoppingCart.bookings;
        const base = [];

        // shopping cart
        for (const booking of bookings) {
          for (const booking_room of booking.booking_rooms) {
            if (parseInt(booking_room.room.id) !== parseInt(s.room.room.id)) continue;
            base.push({
              start: moment(booking.start).toDate(),
              end: moment(booking.end).add(-1, 'd').toDate()
            });
          }
        }

        // room external bookings
        base.push(...s.room.room.lock_intervals.map((interval) => {
          const start = moment(interval.from);
          const end = moment(interval.to);
          if (!interval.can_end_before) {
            start.add(-1, 'd');
          }
          if (interval.can_start_after) {
            end.add(-1, 'd');
            if (end.unix() < start.unix()) return null;
          }
          return {
            start: start.toDate(),
            end: end.toDate(),
          }
        }).filter((item) => !!item));

        base.push({end: moment().add(-1, 'd').toDate()});
        return base;
      },
      pickerTravelers: s => s.search.search.travelers
    }),
    lockIntervalsEnd() {
      const s = this.$store.state;
      const bookings = s.shoppingCart.bookings;
      const intervals = [];

      // shopping cart
      for (const booking of bookings) {
        for (const booking_room of booking.booking_rooms) {
          if (parseInt(booking_room.room.id) !== parseInt(s.room.room.id)) continue;
          intervals.push({
            start: moment(booking.start).add(1, 'd').toDate(),
            end: moment(booking.end).toDate(),
            can_end_before: true,
            can_start_after: true,
          });
        }
      }

      intervals.push({end: moment(this.start).toDate()});
      intervals.push(...s.room.room.lock_intervals.map((interval) => {
        const start = moment(interval.from);
        const end = moment(interval.to);
        if (interval.can_end_before && start.isAfter(this.start)) {
          start.add(1, 'd')
          end.add(1, 'd')
        }
        if (this.start.isAfter(end)) return null;
        return {
          start: start.toDate(),
          end: end.toDate(),
          can_end_before: interval.can_end_before,
          can_start_after: interval.can_start_after,
        }
      }).filter(item => !!item));

      return intervals;
    },
    endIntervalReduced() {
      let min = null;
      for (const interval of this.lockIntervalsEnd) {
        if (!interval.start) continue;
        if (moment(interval.start).isBefore(moment(this.start).add(1, 'd'))) continue;
        if (!min || moment(interval.start).isBefore(min)) min = moment(interval.start);
      }
      const result = [];
      if (this.start)
        result.push({end: this.start.toDate()});
      if (min)
        result.push({start: min/*.add(1, 'd')*/.toDate()})
      return result;
    },
    filtered () {
      return this.$store.getters["resaPicker/isFiltered"];
    },
    nightCount () {
      if (this.start && this.end)
        return Math.abs(this.start.diff(this.end, 'd'));
      return 0;
    },
    travelers () {
      const babies = this.pickerTravelers.babies;
      let adults = this.pickerTravelers.adults;
      let children = this.pickerTravelers.children;

      while ((adults + children) > this.roomMaxTravelers) {
        if (children > 0) {
          children -= 1;
          continue;
        }
        adults -= 1;
      }

      return {
        adults, children, babies
      }
    },
    canBook() {
      return this.pickedPrice === null && this.start && this.end;
    },
    nightCost() {
      const price = this.$store.getters["resaPicker/prices"];
      if (!price || price.length === 0) return 0;
      return price.reduce((prev, curr) => {
        const value = curr.input.reduce((p, n) => {
          return (p === 0 || p > (n.hostingPrice + n.additionalPrice)) ? (n.hostingPrice + n.additionalPrice) : p;
        }, 0);
        return prev === 0 || value < prev ? value : prev;
      }, 0);
    },
    startDate() {
      return this.start ? this.start.format('LL') : this.$t('room.picker.form.add-date');
    },
    endDate() {
      return this.end ? this.end.format('LL') : this.$t('room.picker.form.add-date');
    },
    hasPrice() {
      return this.computedPrices.length !== 0;
    },
    pickerOpen() {
      return this.pickerMode !== null;
    },
    adults() {
      return this.travelers.adults;
    },
    children() {
      return this.travelers.children;
    },
    babies() {
      return this.travelers.babies;
    },
    minBabies() {
      return 0;
    },
    minAdults() {
      return 1;
    },
    minChildren() {
      return 0;
    },
    maxAdults() {
      return this.roomMaxTravelers - this.children;
    },
    maxChildren() {
      return this.roomMaxTravelers - this.adults;
    },
    maxBabies() {
      return 1;
    },
    travelerCount() {
      return this.children + this.adults;
    },
    computedPrices() {
      return this.$store.getters["resaPicker/priceTotal"];
    },
    ...mapState({
      loading: s => s.resaPicker.loading
    })
  },
  data() {
    return {
      prices: [],
      start: null,
      end: null,
      pickerMode: null,
      travelersOpen: false,
      calendarAttributes: [],
      pickedPrice: null,
      autoLoad: true
    }
  },
  watch: {
    roomMaxTravelers: (v) => {
      while (v < this.travelers.adults + this.travelers.children) {
        if (this.travelers.children > 0) {
          this.addChildren(-1);
          continue;
        }
        this.addAdults(-1);
      }
    }
  },
  mounted() {
    this.pickPrice(null);
    this.$store.commit('resaPicker/SET_API_RESULT', null);
    this.$store.commit('resaPicker/SET_LOADING', false);
  },
  methods: {
    async book() {
      const data = {
        start: this.start.toISOString(true),
        end: this.end.toISOString(true),
        room_id: this.roomId,
        room_price_id: this.pickedPrice.id,
        nb_guests: this.$store.getters["search/nbGuests"],
        price_options: [],
      };
      const bookingId = await this.$store.dispatch('shoppingCart/booking/add', data);
      await this.$router.push({
        name: 'step3',
        params: {
          shoppingCart: this.$store.state.shoppingCart.uuid,
          shoppingBookingId: bookingId
        }
      });
    },
    needBackground(color) {
      const ratio = contrastRatio(color, '#ffffff');
      return ratio > .5;
    },
    pickPrice(price) {
      this.pickedPrice = price;
    },
    setPicker(v) {
      if (v === 'end' && !this.start) return;
      this.pickerMode = v;
    },
    openTravelers() {
      this.travelersOpen = true;
    },
    closeTravelers() {
      this.travelersOpen = false;
    },
    addBabies(inc = 1) {
      if (this.travelers.babies + inc < 0 || this.travelers.babies + inc > 1) return;
      this.$store.commit('search/SET_SEARCH_TRAVELERS', {
        adults: this.travelers.adults,
        children: this.travelers.children,
        babies: this.travelers.babies + inc,
      })
      if (this.hasPrice || this.autoLoad) {
        this.sendCheckDisponibilities();
      }
    },
    addChildren(inc = 1) {
      if (inc > 0 && this.maxChildren < this.travelers.children + inc) return;
      if (inc < 0 && this.travelers.children + inc < 0) return;
      this.$store.commit('search/SET_SEARCH_TRAVELERS', {
        adults: this.travelers.adults,
        children: this.travelers.children + inc,
        babies: this.travelers.babies,
      })
      if (this.hasPrice || this.autoLoad) {
        this.sendCheckDisponibilities();
      }
    },
    addAdults(inc = 1) {
      if (inc > 0 && this.maxAdults < this.travelers.adults + inc) return;
      if (inc < 0 && this.travelers.adults + inc < 1) return;
      this.$store.commit('search/SET_SEARCH_TRAVELERS', {
        adults: this.travelers.adults + inc,
        children: this.travelers.children,
        babies: this.travelers.babies,
      })
      if (this.hasPrice || this.autoLoad) {
        this.sendCheckDisponibilities();
      }
    },
    openPicker(type) {
      if (type === 'start' || !this.start) {
        this.pickerMode = 'start';
        return;
      }
      this.pickerMode = type;
    },
    closePicker() {
      this.pickerMode = null;
    },
    pushDateStart(date) {
      this.calendarAttributes = this.calendarAttributes.filter((attr) => {
        return attr.key !== 'start';
      })
      this.calendarAttributes.push({
        key: 'start',
        dates: date.toDate(),
        highlight: {
          color: 'gray',
          fillMode: 'solid'
        }
      });
      this.buildIntermediates();
    },
    pushDateEnd(date) {
      this.calendarAttributes = this.calendarAttributes.filter((attr) => {
        return attr.key !== 'end';
      })
      this.calendarAttributes.push({
        key: 'end',
        dates: date.toDate(),
        highlight: {
          color: 'gray',
          fillMode: 'outline'
        }
      })
      this.buildIntermediates();
    },
    buildIntermediates() {
      const startValues = this.calendarAttributes.filter((attr) => attr.key === 'start');
      const endValues = this.calendarAttributes.filter((attr) => attr.key === 'end');
      this.calendarAttributes = this.calendarAttributes.filter((attr) => attr.key !== 'int');
      if (startValues.length === 0 || endValues.length === 0) return;
      const fromDate = moment(startValues[0].dates);
      const endDate = moment(endValues[0].dates);
      if (fromDate.isAfter(endDate) || fromDate.format('DDMMYYYY') === endDate.format('DDMMYYYY')) return;
      this.calendarAttributes.push({
        key: 'int',
        highlight: {
          color: 'gray',
          fillMode: 'light'
        },
        dates: {
          start: fromDate.toDate(),
          end: endDate.toDate()
        }
      })
    },
    handleDayHover(value) {
      const date = moment(value.id);
      if (this.pickerMode === 'start') {
        this.pushDateStart(date);
        return;
      }
      if (this.pickerMode === 'end') {
        if (!this.isDateValid('end', date)) return;
        this.pushDateEnd(date);
      }
    },
    isDateValid(type, date) {
      const tmp = moment(date).format('YYYY-MM-DD');
      const validIntervals = type === 'start' ? this.lockIntervalsStart : this.endIntervalReduced;
      for (const interval of validIntervals) {
        const istart = moment(interval.start).format('YYYY-MM-DD');
        const iend = moment(interval.end).format('YYYY-MM-DD');
        if (interval.start && !interval.end) {
          if (tmp >= istart) return false;
        }
        if (interval.end && !interval.start) {
          if (tmp <= iend) return false;
        }
        if (interval.end && interval.start) {
          if (tmp >= istart && tmp <= iend) return false;
        }
      }
      return true;
    },
    handlePickerData(value) {
      const date = moment(value.id);
      if (!this.isDateValid(this.pickerMode, date)) return;
      if (this.pickerMode === 'start') {
        this.start = date;
        this.pickerMode = 'end';
        this.pushDateStart(date)
        this.end = null;
        return;
      }
      if (this.pickerMode === 'end') {
        this.end = date;
        this.pickerMode = null;
        this.pushDateEnd(date);
        if (this.hasPrice || this.autoLoad) {
          this.sendCheckDisponibilities();
        }
      }
    },
    async sendCheckDisponibilities() {
      if (!this.start || !this.end) {
        window.location.reload();
        return; // Important pour ne pas continuer l'exécution
      }
      this.pickPrice(null);
      const data = {
        start: this.start,
        end: this.end,
        travelers: {
          adults: this.travelers.adults,
          babies: this.travelers.babies,
          children: this.travelers.children,
        },
        id: this.roomId,
        lang: this.$store.state.lang.currentLang
      }
      await this.$store.dispatch('resaPicker/query', data);
    }
  }
}
</script>

<style scoped>
  .price-info {
    margin: 0;
    display: block;
    text-align: right;
    font-style: italic;
    font-size: .75em;
    opacity: .7;
  }
  .offer-price {
    line-height: 1;
  }
  .info-block.errored {
    background: white;
    color: var(--darker-orange);
    border: 1px solid var(--darker-orange);
    padding: 12px;
    border-radius: 6px;
    margin: 12px 0 0 0;
    font-size: .9em;
    font-weight: 600;
    font-family: "Sofia Pro Extra", sans-serif;
  }
  .info-block p {
    margin: 0;
  }
</style>



// WEBPACK FOOTER //
// src/pages/rooms/components/resa-picker.vue